import React from 'react'

import { pxToRem } from '@/shared/styles/themes'
import { privacyData } from '@/slices/Help/mocks/privacy/data'
import { Box, Container, styled, Typography } from '@mui/material'

export type PrivacyPolicySliceProps = {
  lastUpdated?: string
  contactEmail?: string
}

export const PrivacyPolicySlice: React.FC<PrivacyPolicySliceProps> = ({
  lastUpdated = 'March 26, 2026',
}) => {
  return (
    <StyledPageWrapper>
      <Container maxWidth="md">
        <StyledHeader>
          <StyledTitle variant="h1">
            Privacy Policy
          </StyledTitle>
          <StyledSubtitle variant="body1">
            Last updated: {lastUpdated}
          </StyledSubtitle>
        </StyledHeader>

        <StyledPrivacyContent>
          {privacyData.map((section) => (
            <StyledSection key={section.id}>
              <StyledSectionTitle variant="h2">
                {section.id}. {section.title}
              </StyledSectionTitle>
              {section.content.map((paragraph, index) => (
                <StyledParagraph key={index} variant="body1">
                  {paragraph}
                </StyledParagraph>
              ))}
              {section.subsections && section.subsections.map((subsection, subIndex) => (
                <StyledSubsection key={subIndex}>
                  <StyledSubsectionTitle variant="h3">
                    {subsection.title}
                  </StyledSubsectionTitle>
                  {subsection.content.map((paragraph, paraIndex) => (
                    <StyledParagraph key={paraIndex} variant="body1">
                      {paragraph}
                    </StyledParagraph>
                  ))}
                </StyledSubsection>
              ))}
            </StyledSection>
          ))}
        </StyledPrivacyContent>

        <StyledFooterSection>
          <Typography variant="h6" gutterBottom>
            Questions about our privacy policy?
          </Typography>
          <Typography variant="body1">
            Contact us at{' '}
            <StyledLink href="mailto:support@pixie.education">
              support@pixie.education
            </StyledLink>
          </Typography>
        </StyledFooterSection>
      </Container>
    </StyledPageWrapper>
  )
}

const StyledPageWrapper = styled(Box)(({ theme }) => ({
  paddingTop: pxToRem(40),
  paddingBottom: pxToRem(80),

  [theme.breakpoints.up('md')]: {
    paddingTop: pxToRem(60),
    paddingBottom: pxToRem(120),
  },
}))

const StyledHeader = styled(Box)(({ theme }) => ({
  textAlign: 'center',
  marginBottom: pxToRem(40),

  [theme.breakpoints.up('md')]: {
    marginBottom: pxToRem(60),
  },
}))

const StyledTitle = styled(Typography)(({ theme }) => ({
  color: theme.palette.primary.main,
  marginBottom: pxToRem(16),
  fontSize: pxToRem(32),
  fontWeight: 700,

  [theme.breakpoints.up('md')]: {
    fontSize: pxToRem(48),
    marginBottom: pxToRem(20),
  },
}))

const StyledSubtitle = styled(Typography)(({ theme }) => ({
  color: theme.palette.primary.main,
  fontSize: pxToRem(14),
  opacity: 0.7,

  [theme.breakpoints.up('md')]: {
    fontSize: pxToRem(16),
  },
}))

const StyledPrivacyContent = styled(Box)(({ theme }) => ({
  marginBottom: pxToRem(60),

  [theme.breakpoints.up('md')]: {
    marginBottom: pxToRem(80),
  },
}))

const StyledSection = styled(Box)(({ theme }) => ({
  marginBottom: pxToRem(32),

  '&:last-child': {
    marginBottom: 0,
  },

  [theme.breakpoints.up('md')]: {
    marginBottom: pxToRem(40),
  },
}))

const StyledSectionTitle = styled(Typography)(({ theme }) => ({
  color: theme.palette.primary.main,
  fontSize: pxToRem(20),
  fontWeight: 600,
  marginBottom: pxToRem(12),

  [theme.breakpoints.up('md')]: {
    fontSize: pxToRem(24),
    marginBottom: pxToRem(16),
  },
}))

const StyledSubsection = styled(Box)(({ theme }) => ({
  marginTop: pxToRem(16),
  marginLeft: pxToRem(8),

  [theme.breakpoints.up('md')]: {
    marginLeft: pxToRem(12),
  },
}))

const StyledSubsectionTitle = styled(Typography)(({ theme }) => ({
  color: theme.palette.primary.main,
  fontSize: pxToRem(18),
  fontWeight: 600,
  marginBottom: pxToRem(10),

  [theme.breakpoints.up('md')]: {
    fontSize: pxToRem(20),
    marginBottom: pxToRem(12),
  },
}))

const StyledParagraph = styled(Typography)(({ theme }) => ({
  color: theme.palette.primary.main,
  fontSize: pxToRem(14),
  lineHeight: 1.7,
  marginBottom: pxToRem(12),

  '&:last-child': {
    marginBottom: 0,
  },

  [theme.breakpoints.up('md')]: {
    fontSize: pxToRem(16),
    marginBottom: pxToRem(14),
  },
}))

const StyledFooterSection = styled(Box)(({ theme }) => ({
  textAlign: 'center',
  padding: pxToRem(15),
  backgroundColor: theme.palette.background.paper,
  borderRadius: pxToRem(12),
  border: `1px solid ${theme.palette.divider}`,

  '& h6': {
    color: theme.palette.primary.main,
    fontWeight: 600,
  },

  '& .MuiTypography-body1': {
    color: theme.palette.primary.main,
  },

  [theme.breakpoints.up('md')]: {
    padding: pxToRem(32),
  },
}))

const StyledLink = styled('a')(({ theme }) => ({
  color: theme.palette.primary.main,
  textDecoration: 'none',
  fontWeight: 600,

  '&:hover': {
    textDecoration: 'underline',
  },
}))
